import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import { useForm } from 'react-hook-form';
import { formActions, httpRequestsOnSuccessActions } from 'store';
import {
    CreateChancel,
    Loader,
    MinLoader,
} from 'components';
import {
    FindLoad,
    FindSuccessItem,
    Images,
    useModal,
    noteStatusEnums, RolePermission, PermissionList, User,
} from 'utils';
import { ReportBuilder } from './progressReport/reportBuilder';
import { FormBuilderProvider, useFormBuilder } from './formDetail';
import { useAutoFillMentions } from './hooks/useAutoFillMentions';
import { isApptNoteModel, pickInitialTemplate, useNoteEditPermissions } from './constants';
import { NoteTemplateField } from './noteTemplateField';

const DEFAULT_FORM_ACTION_TYPE = 'GET_FORM_BY_ID';
const DEFAULT_FORM_VALUES_ACTION_TYPE = 'GET_FORMS_DEFAULT';
const FORM_ACTION_TYPE = 'GET_FORMS';

const SessionNoteFormBuilderInner = forwardRef(
    (
        {
            info,
            resourceId,
            formModel,
            afterSuccess,
            ACTION_TYPE,
            handleSaveNote,
            handleSaveAction,
            sendParams = {},
            width,
            defaultForm,
            actionName,
            inviteToken,
            renderCustomFields,
            serviceTemplate,
            statusRef,
            appt,
            onCancel,
        },
        ref,
    ) => {
        const { setFormList, setImageUrls } = useFormBuilder();
        const {
            handleSubmit,
            watch,
            control,
            setValue,
            getValues,
            reset,
        } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });
        const forms = useSelector((state) => state.forms.forms)?.data;
        const formById = useSelector((state) => state.forms.form);
        const formDefault = useSelector((state) => state.forms.formDefault);
        const dispatch = useDispatch();
        const loader = FindLoad(ACTION_TYPE);
        const loadForm = FindLoad(DEFAULT_FORM_ACTION_TYPE);
        const loadDefaults = FindLoad(DEFAULT_FORM_VALUES_ACTION_TYPE);
        const success = FindSuccessItem(ACTION_TYPE);
        const { close } = useModal();
        // Dismissing the form means closing the modal it lives in — unless a host renders it
        // inline (session Documentation), in which case `close()` would be a dead click and the
        // host says what "cancel" does instead.
        const handleCancel = onCancel || close;
        const [isDraftSaving, setIsDraftSaving] = useState(false);
        const formRef = useRef();
        const reportBuilderRef = useRef(null);
        const applyAutoFill = useAutoFillMentions({ setValue, getValues });
        const isLoading = !!loadForm?.length || !!loadDefaults?.length;
        // const { userInfo } = User();
        // const needApproved = info?.template?.formSettings?.needApproved || formById?.formSettings?.needApproved;
        // const isLocked =  info?.template?.formSettings?.lockNoteAfterSubmit || formById?.formSettings?.lockNoteAfterSubmit;
        // const isUpdateRequest = info?.id && info?.status === noteStatusEnums.UPDATE_REQUEST;
        // const checkSuperVizer = appt?.authorizedService?.renderingProvider?._id === userInfo?.id ? true : RolePermission([PermissionList.APPT_UPDATE_NOTES?.code]);
        const { canEdit, checkSuperVizer, isUpdateRequest, isLocked, needApproved } = useNoteEditPermissions(info, appt)
        const showDraftSaveBtn =  checkSuperVizer ?  true : (info?.id ?  info?.status === noteStatusEnums.DRAFT : true)
        const [onLoadFile, setOnLoadFile] = useState(false)

        // const showCloseBtn =
        //         (info?.status === noteStatusEnums.IN_REVIEW && !checkSuperVizer) ? true :
        //            (info?.status === noteStatusEnums.COMPLETED && isLocked && !checkSuperVizer) ? true :
        //                (info?.status === noteStatusEnums.DRAFT && userInfo?.id !== info?.creatorId) ? true :
        //                    isUpdateRequest && userInfo?.id !== info?.creatorId ? true : false;


        const getStatusForAction = (action) => {
            if (action === noteStatusEnums.DRAFT) {
                return noteStatusEnums.DRAFT;
            }
            else if (action === noteStatusEnums.COMPLETED) {
                // Editing an already-COMPLETED note → keep COMPLETED. The user is saving
                // content edits, not re-submitting; sending IN_REVIEW here would push the
                // note backwards in the workflow when needApproved is on.
                if (info?.id && info?.status === noteStatusEnums.COMPLETED) {
                    return noteStatusEnums.COMPLETED;
                }
                if (checkSuperVizer){
                    return noteStatusEnums.COMPLETED;
                } else{
                    if (needApproved) {
                        return noteStatusEnums.IN_REVIEW;
                    }else{
                        return noteStatusEnums.COMPLETED;
                    }
                }

            }
        };

        useImperativeHandle(ref, () => ({
            submit: () => {
                handleSubmit(
                    (data) => handleCreate(data),
                    (errors) => {
                        scrollToFirstError(errors);
                    },
                )();
            },
            submitDraft: () => {
                handleSaveDraft();
            },
            isDirty: () => !!reportBuilderRef.current?.isDirty?.(),
            canAutoSave: () =>
                !!reportBuilderRef.current?.isDirty?.() &&
                !canEdit &&
                (!info?.id || info?.status === noteStatusEnums.DRAFT),
            setFieldByPath: (path, value) => {
                return reportBuilderRef.current?.setFieldByPath?.(path, value) ?? false;
            },
        }));

        useEffect(() => {
            if (formById?.id) {
                // Signed rich-text image URLs live in context, NOT formList/RHF
                // values (they change every read — see formContext imageUrls note).
                // Without this the ReportBuilder's imageUrls map stays empty on the
                // getFormById load path (auto-selected / switched template), so
                // resolveRichTextImages can't inject src into <img data-key> tags
                // and rich-text images fail to render.
                const { imageUrls: formImageUrls, ...cloned } = cloneDeep(formById);
                setImageUrls?.(formImageUrls || {});
                setFormList(cloned);
                const params = {
                    customForm: watch('customForm'),
                    formId: formById?.id,
                    ...cloned,
                };
                reset(params);
                applyAutoFill();
            }
        }, [formById]);

        const selectTemplate = (formId) => {
            setValue('customForm', formId);
            const params = {
                model: formModel,
                resourceId: resourceId,
                formId,
                ...sendParams,
            };
            dispatch(formActions.getFormDefault(formId, params));
            // Third arg only for session notes: it is the appt whose
            // collected session data fills SESSION_DATA_TABLE blocks.
            dispatch(formActions.getFormById(formId, params, isApptNoteModel(formModel) ? resourceId : undefined));
        };

        useEffect(() => {
            if (forms?.length && !watch('customForm') && !info?.id) {
                const selectedForm = pickInitialTemplate(forms, serviceTemplate);
                if (selectedForm) selectTemplate(selectedForm.id);
            }
        }, [forms]);

        useEffect(() => {
            const formId = info?.template?.formId;
            if (!info?.id || !formId) return;
            dispatch(formActions.resetFormDefault());
            dispatch(formActions.getFormDefault(formId, { model: formModel, resourceId, formId, ...sendParams }));
        }, [info?.id]);

        useEffect(() => {
            if (info?.template) {
                // imageUrls belongs in context (they're per-request signed
                // URLs, not template state), so extract before feeding
                // formList — otherwise they'd falsely mark the form dirty
                // on every refresh.
                const { imageUrls: infoImageUrls, ...tpl } = info.template || {};
                setImageUrls?.(infoImageUrls || {});
                setFormList(cloneDeep(tpl));
                const params = {
                    ...tpl,
                    customForm: info?.id,
                };
                reset(params);
                applyAutoFill();
            }
            if (defaultForm) {
                const { imageUrls: dfImageUrls, ...df } = defaultForm || {};
                setImageUrls?.(dfImageUrls || {});
                setFormList(cloneDeep(df));
                reset({
                    customForm: df?.id,
                    formId: df?.id,
                    ...df,
                });
                applyAutoFill();
            }
        }, [info, defaultForm]);

        useEffect(() => {
            if (success) {
                close();
                dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));

                if (afterSuccess) {
                    afterSuccess();
                }
            }
        }, [success]);

        const handleCreate = () => {
            reportBuilderRef.current?.submit();
        };

        const handleSaveDraft = () => {
            setIsDraftSaving(true);

            if (info?.status === noteStatusEnums.UPDATE_REQUEST){
                statusRef.current = getStatusForAction(noteStatusEnums.UPDATE_REQUEST);
            }else {
                if (info?.status === noteStatusEnums.IN_REVIEW) {
                    statusRef.current = getStatusForAction(noteStatusEnums.IN_REVIEW);
                } else {
                    statusRef.current = getStatusForAction(noteStatusEnums.DRAFT);
                }
            }

            reportBuilderRef.current?.submitDraft();
        };

        const scrollToFirstError = (errors) => {
            if (!errors?.attributes && !errors?.sections) return;

            const flattenErrors = (data, path) => {
                if (data?.sections) {
                    for (let sIndex = 0; sIndex < data?.sections.length; sIndex++) {
                        const section = data?.sections[sIndex];
                        if (!section) continue;

                        if (section.attributes) {
                            const attrPath = flattenErrors(
                                { attributes: section?.attributes },
                                `${path || 'sections'}.${sIndex}.attributes`,
                            );
                            if (attrPath) return attrPath;
                        }
                    }
                }

                if (data?.attributes) {
                    const attributes = data.attributes;
                    for (let i = 0; i < attributes.length; i++) {
                        const item = attributes[i];
                        if (!item) continue;

                        const currentPath = `${path ? path : 'attributes'}.${i}`;

                        if (item?.value) return `${currentPath}.value`;

                        if (item?.fields) {
                            for (let fIndex = 0; fIndex < item?.fields?.length; fIndex++) {
                                const f = item.fields[fIndex];
                                if (f?.value) return `${currentPath}.fields.${fIndex}.value`;
                            }
                        }

                        if (item.children) {
                            const childPath = flattenErrors({ attributes: item.children }, `${currentPath}.children`);
                            if (childPath) return childPath;
                        }
                    }
                }
                return null;
            };

            const firstErrorField = flattenErrors(errors);

            if (firstErrorField) {
                // Or the first input nested under that value — a Session Data Table's
                // manual cells are named `<value>.<row>[.trials.<i>].manual.<column>`.
                const field = document.querySelector(`[name="${firstErrorField}"], [name^="${firstErrorField}."]`);
                if (field?.scrollIntoView) {
                    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    field.focus({ preventScroll: true });
                } else {
                    formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        };

        return (
            <div style={{ width: width ? width : 816 }}>
                <div className="form-builder">
                    <form
                        onSubmit={handleSubmit(
                            (data) => handleCreate(data),
                            (errors) => {
                                scrollToFirstError(errors);
                            }
                        )}>
                        {!defaultForm && (
                            <>
                                <NoteTemplateField
                                    serviceTemplate={serviceTemplate}
                                    info={info}
                                    forms={forms}
                                    control={control}
                                    loadType={FORM_ACTION_TYPE}
                                    onSelect={selectTemplate}
                                    onClear={() => dispatch(formActions.resetFormDefault())}
                                />
                                {renderCustomFields && <>{renderCustomFields(control, watch('customForm'))}</>}
                            </>
                        )}

                        {isLocked && (
                            <div className="session-note-locked-banner">
                                <img src={Images.lockGray} alt="lock" />
                                <p>
                                    This note will be locked after submission. Contact a supervisor to request changes.
                                   {/*This note has been submitted and is locked. Contact a supervisor to request changes.*/}
                                </p>
                            </div>
                        )}

                        {isUpdateRequest && (
                            <div className="session-note-update-banner">
                                <div className="session-note-update-banner-header">
                                    <img src={Images.warningInfo} alt="warning" />
                                    <p>Update Requested</p>
                                </div>
                                {info?.requestedMessage && <p className="session-note-update-banner-message">{info.requestedMessage}</p>}
                            </div>
                        )}

                        {isLoading ? (
                            <Loader height={'200px'} />
                        ) : (
                            watch('customForm') && (
                                <ReportBuilder
                                    resourceId={resourceId}
                                    ref={reportBuilderRef}
                                    handleSaveNote={handleSaveNote}
                                    statusRef={statusRef}
                                    noteStatus={info?.status}
                                    onSigUploadingChange={(e) => setOnLoadFile(e)}
                                />
                            )
                        )}

                        {canEdit ? (
                            <div className="submit-form-actions">
                                <CreateChancel
                                    noChancel
                                    type="button"
                                    create={'Close'}
                                    onClose={handleCancel}
                                    onCreate={handleCancel}
                                    buttonWidth="100%"
                                />
                            </div>
                        ) : (
                            <div className="submit-form-actions">
                                <CreateChancel
                                    disabled={isLoading || !!loader.length || onLoadFile}
                                    type="submit"
                                    loader={(!!loader.length && !isDraftSaving) || (onLoadFile && !isDraftSaving)}
                                    create={
                                        info?.id && info?.status === noteStatusEnums.COMPLETED
                                            ? 'Save Changes'
                                            : info?.id && info?.status === noteStatusEnums.UPDATE_REQUEST
                                            ? 'Resubmit'
                                            : getStatusForAction(noteStatusEnums.COMPLETED) === noteStatusEnums.IN_REVIEW
                                            ? 'Submit for Review'
                                            : info?.status === noteStatusEnums.IN_REVIEW
                                            ? 'Approve'
                                            : 'Submit'
                                    }
                                    chancel={'Cancel'}
                                    onClose={handleCancel}
                                    onCreate={() => {
                                        setIsDraftSaving(false);
                                        statusRef.current = getStatusForAction(noteStatusEnums.COMPLETED);
                                    }}
                                    buttonWidth="100%"
                                    renderSecondAction={() =>
                                        showDraftSaveBtn &&
                                        info?.status !== noteStatusEnums.COMPLETED && (
                                            <button
                                                type="button"
                                                onClick={handleSaveDraft}
                                                className="save-as-draft-btn"
                                                disabled={isLoading || !!loader.length}
                                            >
                                                {(!!loader.length || onLoadFile) && isDraftSaving ? (
                                                    <MinLoader margin={'0'} color={'#0B5DCF'} position={'relative'} />
                                                ) : info?.id ? (
                                                    'Save Changes'
                                                ) : (
                                                    'Save as Draft'
                                                )}
                                            </button>
                                        )
                                    }
                                />
                            </div>
                        )}
                    </form>
                </div>
            </div>
        );
    },
);

export const SessionNoteFormBuilder = forwardRef((props, ref) => (
    <FormBuilderProvider>
        <SessionNoteFormBuilderInner {...props} ref={ref} />
    </FormBuilderProvider>
));
